import React from 'react';
import { FileBarChart } from 'lucide-react';
import { Accordion } from '@/components/ui/accordion';
import { SectionAccordionItem } from '../cards';
import { BulkActionsDropdown } from '../BulkActionsDropdown';
import { ReportAccessBlock } from './ReportAccessBlock';

export interface ReportsTabProps {
  enableTimesheetReport: boolean;
  onEnableTimesheetReportChange: (value: boolean) => void;
  viewTimesheetReportAccess: string;
  onViewTimesheetReportAccessChange: (value: string) => void;
  allowTimesheetReportExport: boolean;
  onAllowTimesheetReportExportChange: (value: boolean) => void;
  enableMissingTimesheetReport: boolean;
  onEnableMissingTimesheetReportChange: (value: boolean) => void;
  viewMissingTimesheetReportAccess: string;
  onViewMissingTimesheetReportAccessChange: (value: string) => void;
  allowMissingTimesheetReportExport: boolean;
  onAllowMissingTimesheetReportExportChange: (value: boolean) => void;
  enableUtilisationReport: boolean;
  onEnableUtilisationReportChange: (value: boolean) => void;
  viewUtilisationReportAccess: string;
  onViewUtilisationReportAccessChange: (value: string) => void;
  allowUtilisationReportExport: boolean;
  onAllowUtilisationReportExportChange: (value: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const ReportsTab: React.FC<ReportsTabProps> = (props) => (
  <>
    <BulkActionsDropdown onSelectAll={props.onSelectAll} onDeselectAll={props.onDeselectAll} />
    <Accordion type="multiple" defaultValue={['report-access', 'analytics-insights']} className="space-y-3">

      <SectionAccordionItem value="report-access" icon={<FileBarChart className="h-5 w-5" />} title="Report Access" description="Control access to various reports and analytics">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

          <ReportAccessBlock
            id="enable-timesheet-report"
            label="Timesheet Report"
            description="Access to detailed timesheet reports showing time entries, projects, and tasks"
            enabled={props.enableTimesheetReport}
            onEnabledChange={props.onEnableTimesheetReportChange}
            viewLabel="View Timesheet Report"
            viewDescription="Define which timesheet data users can view in reports"
            viewAccess={props.viewTimesheetReportAccess}
            onViewAccessChange={props.onViewTimesheetReportAccessChange}
            exportDescription="Allow exporting timesheet report data to Excel or CSV"
            allowExport={props.allowTimesheetReportExport}
            onAllowExportChange={props.onAllowTimesheetReportExportChange}
          />

          <ReportAccessBlock
            id="enable-missing-report"
            label="Missing Timesheet Report"
            description="Access to reports showing employees with missing or incomplete timesheets"
            enabled={props.enableMissingTimesheetReport}
            onEnabledChange={props.onEnableMissingTimesheetReportChange}
            viewLabel="View Missing Timesheet Report"
            viewDescription="Define which missing timesheet data users can view in reports"
            viewAccess={props.viewMissingTimesheetReportAccess}
            onViewAccessChange={props.onViewMissingTimesheetReportAccessChange}
            exportDescription="Allow exporting missing timesheet report data to Excel or CSV"
            allowExport={props.allowMissingTimesheetReportExport}
            onAllowExportChange={props.onAllowMissingTimesheetReportExportChange}
          />

          <ReportAccessBlock
            id="enable-utilisation-report"
            label="Utilisation Report"
            description="Access to utilisation reports showing resource allocation and capacity metrics"
            enabled={props.enableUtilisationReport}
            onEnabledChange={props.onEnableUtilisationReportChange}
            viewLabel="View Utilisation Report"
            viewDescription="Define which utilisation data users can view in reports"
            viewAccess={props.viewUtilisationReportAccess}
            onViewAccessChange={props.onViewUtilisationReportAccessChange}
            exportDescription="Allow exporting utilisation report data to Excel or CSV"
            allowExport={props.allowUtilisationReportExport}
            onAllowExportChange={props.onAllowUtilisationReportExportChange}
          />

          {/* billableHoursReport removed — belongs in the Projects module of the PSA suite. */}

        </div>
      </SectionAccordionItem>
    </Accordion>
  </>
);
